import { FlattUtil } from '@mlc/shared/utils';
import { NameUtil } from '@mlc/shared/utils/name-util';
import { addDays, differenceInYears, format } from 'date-fns';
import { ComposedCaseData, RawCaseNodes } from './case-data.model';

export class CaseDataTransformer {
  private static readonly UNWANTED_NODES = [
    'encounters',
    'orders',
    'filestore',
    'reports',
    'notes',
    'pos-reports',
    'audit-logs',
    'history-notes',
    'historyOrder',
  ];

  /**
   * Transforms raw case nodes into a dual-key ComposedCaseData dictionary:
   * 1. Raw structured trees (patient, org, provider, appointment, facility, lastEncounter, workStatus, historyForms)
   * 2. Canonical semantic camelCase aliases for PDF AcroForms
   * 3. Flattened underscore paths for HTML TinyMCE templates (FlattUtil.is)
   */
  public static transform(nodes: RawCaseNodes): ComposedCaseData {
    const {
      patient = {},
      orgData = {},
      doctorData = null,
      selectedAppointment = null,
      facilityData = null,
      latestEncounter = null,
      historyForms = {},
      workStatus = {},
      user = {},
      doctorSignature = null,
      userSignature = null,
    } = nodes;

    const now = new Date();

    // 1. System Dates & Age
    const sysDate = CaseDataTransformer.formatDate(now, 'MM/dd/yyyy');
    const sysDateDisplay = CaseDataTransformer.formatDate(now, 'MMM d, yyyy');
    const sysDate30 = CaseDataTransformer.formatDate(addDays(now, 30), 'MM/dd/yyyy');
    const sysDate45 = CaseDataTransformer.formatDate(addDays(now, 45), 'MM/dd/yyyy');
    const sysDate60 = CaseDataTransformer.formatDate(addDays(now, 60), 'MM/dd/yyyy');
    const sysDate90 = CaseDataTransformer.formatDate(addDays(now, 90), 'MM/dd/yyyy');
    const sysDateDisplay30 = CaseDataTransformer.formatDate(addDays(now, 30), 'MMM d, yyyy');
    const sysDateDisplay45 = CaseDataTransformer.formatDate(addDays(now, 45), 'MMM d, yyyy');
    const sysDateDisplay60 = CaseDataTransformer.formatDate(addDays(now, 60), 'MMM d, yyyy');
    const sysDateDisplay90 = CaseDataTransformer.formatDate(addDays(now, 90), 'MMM d, yyyy');
    const age = patient?.dob && !isNaN(new Date(patient.dob).getTime())
      ? differenceInYears(now, new Date(patient.dob))
      : 0;

    // 2. Patient Demographics & Identification
    const nameInfo = CaseDataTransformer.formatPatientNames(patient);
    const patientFirstName = nameInfo.firstName;
    const patientLastName = nameInfo.lastName;
    const patientMiddleInitial = nameInfo.middleInitial;
    const patientFullName = nameInfo.firstMiddleLast;
    const patientNameLastFirst = nameInfo.lastFirstMi;
    const ssnClean = (patient?.ssn || '').replace(/\D/g, '');
    const ssnFormatted = CaseDataTransformer.formatSSN(patient?.ssn);
    const l4ssn = ssnClean.length >= 4 ? ssnClean.slice(-4) : ssnClean;
    const patientAddress = patient?.address || '';
    const patientCity = patient?.city || '';
    const patientState = patient?.state || '';
    const patientZip = patient?.zip || '';
    const patientAddressCityStZip = [patientAddress, [patientCity, patientState].filter(Boolean).join(', '), patientZip]
      .filter(Boolean)
      .join(' ');
    const cellPhone = CaseDataTransformer.formatPhone(patient?.cellPhone || patient?.phone);
    const otherPhone = CaseDataTransformer.formatPhone(patient?.otherPhone || patient?.homePhone);
    const patientEmail = patient?.email || '';
    const sex = patient?.gender || patient?.sex || '';
    const interpreterRequired = patient?.interpreter || patient?.language ? 'Yes' : 'No';
    const interpreterLanguage = patient?.language || '';
    const interpreter = patient?.interpreter ? 'Yes' : 'No';

    // 3. Organization & Clinic Details
    const orgName = orgData?.orgName || orgData?.name || '';
    const orgAddress = orgData?.orgAddress || orgData?.address || '';
    const orgCity = orgData?.orgCity || orgData?.city || '';
    const orgState = orgData?.orgState || orgData?.state || '';
    const orgZip = orgData?.orgZip || orgData?.zip || '';
    const orgCityStZip = orgData?.orgCityStZip || [orgCity, orgState].filter(Boolean).join(', ') + (orgZip ? ` ${orgZip}` : '');
    const orgPhone = CaseDataTransformer.formatPhone(orgData?.orgPhone || orgData?.contactPhone);
    const orgFax = CaseDataTransformer.formatPhone(orgData?.orgFax || orgData?.fax);
    const orgFullAddress = orgData?.orgFullAddress || [orgAddress, orgCity, orgState, orgZip].filter(Boolean).join(' ');

    // 4. Employment Details
    const emp = patient?.employmentInfo || {};
    const employerName = emp.employer || emp.name || '';
    const employerAddress = emp.address || emp.street || '';
    const employerCity = emp.city || '';
    const employerState = emp.state || '';
    const employerZip = emp.zip || '';
    const employerAddressCityStZip = [employerAddress, [employerCity, employerState].filter(Boolean).join(', '), employerZip]
      .filter(Boolean)
      .join(' ');
    const employerPhone = CaseDataTransformer.formatPhone(emp.phone);
    const patientOccupation = emp.jobTitle || emp.occupation || '';
    const employmentStatus = emp.status || emp.employmentStatus || '';

    // 5. Workers' Compensation Insurance & Claims
    const wc = patient?.workCompInsurance || {};
    const wcInsuranceName = wc.name || wc.insuranceName || '';
    const wcInsuranceAddress = wc.mailingAddressStreet || wc.address || '';
    const wcInsuranceCity = wc.mailingAddressCity || wc.city || '';
    const wcInsuranceState = wc.mailingAddressState || wc.state || '';
    const wcInsuranceZip = wc.mailingAddressZip || wc.zip || '';
    const insuranceNameAddress = [wcInsuranceName, wcInsuranceAddress, wcInsuranceCity, wcInsuranceState, wcInsuranceZip]
      .filter(Boolean)
      .join(', ');
    const claimNumber = wc.claimNo || wc.claimNumber || '';
    const policyNumber = wc.policyNo || wc.policyNumber || '';
    const claimsExaminer = wc.claimsExaminer || wc.adjusterName || '';
    const claimsExaminerPhone = CaseDataTransformer.formatPhone(wc.phoneNo || wc.phone);
    const claimsExaminerFax = CaseDataTransformer.formatPhone(wc.fax);
    const claimsExaminerEmail = wc.email || '';
    const panelNumber =
      patient?.additionalCaseInfo?.panelNumber ||
      patient?.panelNumber ||
      patient?.qmePanelNumber ||
      '';
    const qmePanelNumber = panelNumber;
    const dwcClaimNumber =
      patient?.additionalCaseInfo?.dwcClaimNumber ||
      patient?.dwcClaimNumber ||
      '';
    const ddAssignmentNumber =
      patient?.additionalCaseInfo?.ddAssignmentNumber ||
      patient?.ddAssignmentNumber ||
      '';

    // 6. Case & Injury Details
    const caseType = patient?.caseType || '';
    const caseCategory = patient?.caseCategory || '';
    const doi = CaseDataTransformer.formatDate(patient?.doi || patient?.injuryDate);
    const doa = CaseDataTransformer.formatDate(patient?.doa || patient?.doi || patient?.accidentDate);
    const ctInjuryCheckBox = Boolean(
      patient?.ctInjury || patient?.isCumulativeTrauma || historyForms?.hsInjuryInfo?.isCumulativeTrauma
    );
    const doiEnd = CaseDataTransformer.formatDate(historyForms?.hsInjuryInfo?.doiEnd || patient?.doiEnd);
    const bodyParts = Array.isArray(patient?.bodyParts)
      ? patient.bodyParts.join(', ')
      : (patient?.bodyParts || historyForms?.hsInjuryInfo?.bodyParts || '');
    const injuryDescription =
      historyForms?.hsInjuryInfo?.accidentDescription ||
      historyForms?.hsInjuryInfo?.description ||
      historyForms?.hsInjuryInfo?.injuryDescription ||
      patient?.injuryDescription ||
      patient?.accidentDescription ||
      '';

    // 7. Treating / Evaluating Doctor Details
    const doc = doctorData || {};
    const treatingDoctor = patient?.treatingDoctor
      ? NameUtil.getFormattedDoctorName(patient.treatingDoctor)
      : '';
    const doctorName = doc.displayName || doc.name || treatingDoctor || '';
    const doctorFirstName = doc.firstName || '';
    const doctorLastName = doc.lastName || '';
    const doctorSpecialty = doc.specialty || '';
    const doctorLicense = doc.sln || doc.licenseNumber || doc.license || doc.qmeNumber || '';
    const qmeSln = doc.sln || doc.qmeNumber || doctorLicense || '';
    const doctorNpi = doc.npi || '';
    const doctorPhone = CaseDataTransformer.formatPhone(doc.phone || orgPhone);
    const doctorFax = CaseDataTransformer.formatPhone(doc.fax || orgFax);
    const docDate = sysDate;
    const dateSigned = sysDate;
    const signatureOnFile = 'Signature on file';
    const userSigBase64 = userSignature?.signature?.base64 || '';
    const doctorSigBase64 = doctorSignature?.signature?.base64 || '';

    // 8. Appointment & Facility Details
    const appt = selectedAppointment || {};
    const fac = facilityData || {};
    const appointmentDate = appt.start ? CaseDataTransformer.formatDate(appt.start) : '';
    const appointmentTime = appt.start ? CaseDataTransformer.formatTime(appt.start) : '';
    const appointmentDateTime = appointmentDate && appointmentTime ? `${appointmentDate} - ${appointmentTime}` : appointmentDate;
    const facilityName = fac.name || '';
    const facilityAddress = fac.address || '';
    const facilityCity = fac.city || '';
    const facilityState = fac.state || '';
    const facilityZip = fac.zip ? fac.zip.slice(0, 5) : '';
    const facilityFullAddress = [facilityAddress, [facilityCity, facilityState].filter(Boolean).join(', '), facilityZip]
      .filter(Boolean)
      .join(' ');
    const facilityPhone = CaseDataTransformer.formatPhone(fac.phone);
    const facilityFax = CaseDataTransformer.formatPhone(fac.fax);

    // 9. Attorneys (Applicant & Defense)
    const att = patient?.attorney || {};
    const attorneyName = att.name || att.contactPerson || '';
    const attorneyFirmName = att.firmName || att.firm || '';
    const attorneyAddress = att.address || att.street || '';
    const attorneyCity = att.city || '';
    const attorneyState = att.state || '';
    const attorneyZip = att.zip || '';
    const attorneyPhone = CaseDataTransformer.formatPhone(att.phone);
    const attorneyFax = CaseDataTransformer.formatPhone(att.fax);
    const attorneyEmail = att.email || '';
    const servedAttorneyAddress = [attorneyAddress, [attorneyCity, attorneyState].filter(Boolean).join(', '), attorneyZip]
      .filter(Boolean)
      .join(' ');

    const def = patient?.defenseAttorney || {};
    const defenseAttorneyName = def.name || def.contactPerson || '';
    const defenseAttorneyFirmName = def.firmName || def.firm || '';
    const defenseAttorneyAddress = def.address || def.street || '';
    const defenseAttorneyCity = def.city || '';
    const defenseAttorneyState = def.state || '';
    const defenseAttorneyZip = def.zip || '';
    const defenseAttorneyPhone = CaseDataTransformer.formatPhone(def.phone);
    const defenseAttorneyFax = CaseDataTransformer.formatPhone(def.fax);
    const defenseAttorneyEmail = def.email || '';
    const servedDefenseAttorneyAddress = [defenseAttorneyAddress, [defenseAttorneyCity, defenseAttorneyState].filter(Boolean).join(', '), defenseAttorneyZip]
      .filter(Boolean)
      .join(' ');

    // 10. Clinical Findings & SOAP Notes (Latest Encounter)
    const enc = latestEncounter || {};
    const soap = enc.soapNotes || {};
    const subjective = soap.subjective || enc.subjective || '';
    const objective = soap.objective || enc.objective || '';
    const assessment = soap.planDiscussion || soap.assessment || enc.assessment || patient?.assessmentDetails?.discussion || '';
    const plans = Array.isArray(soap.plans) ? soap.plans : [];
    const primaryDiagnosis = plans[0]?.diagnosis || plans[0]?.description || '';
    const primaryIcd10 = plans[0]?.icd10 || plans[0]?.code || '';
    const secondaryDiagnosis = plans[1]?.diagnosis || plans[1]?.description || '';
    const secondaryIcd10 = plans[1]?.icd10 || plans[1]?.code || '';
    const treatmentPlan = plans.length > 0
      ? plans.map((p: any) => [p.code, p.description, p.notes].filter(Boolean).join(' - ')).join('\n')
      : (soap.plan || enc.plan || '');

    // 11. Work Status & Disability Restrictions
    const ws = workStatus || patient?.workability?.workStatus || {};
    const remainOffWorkCheckBox = Boolean(ws.remainOffWork || ws.remainOffWorkCheckBox);
    const offWorkUntil = ws.offWorkUntil ? CaseDataTransformer.formatDate(ws.offWorkUntil) : '';
    const returnToModifiedWorkCheckBox = Boolean(ws.returnToModifiedWork || ws.returnToModifiedWorkCheckBox);
    const returnToModifiedWorkOn = ws.returnToModifiedWorkOn ? CaseDataTransformer.formatDate(ws.returnToModifiedWorkOn) : '';
    const returnToFullDutyCheckBox = Boolean(ws.returnToFullDuty || ws.returnToFullDutyCheckBox);
    const returnToFullDutyOn = ws.returnToFullDutyOn ? CaseDataTransformer.formatDate(ws.returnToFullDutyOn) : '';
    const workRestrictions = ws.workRestrictions || ws.restrictions || '';
    const maxLiftingWeight = ws.maxLiftingWeight || ws.maxWeight || '';
    const noProlongedSittingCheckBox = Boolean(ws.noProlongedSitting);
    const noProlongedStandingCheckBox = Boolean(ws.noProlongedStanding);
    const noProlongedWalkingCheckBox = Boolean(ws.noProlongedWalking);
    const noTwistingCheckBox = Boolean(ws.noTwisting);
    const noLiftingCheckBox = Boolean(ws.noLifting);

    // 12. User / Staff Info
    const userName = user?.displayName || user?.name || user?.email || '';
    const userEmail = user?.email || '';

    // 13. Date Parts for Split AcroForm Fields (Month, Day, Year)
    const apptDateParts = CaseDataTransformer.extractDateParts(appt.start);
    const dobParts = CaseDataTransformer.extractDateParts(patient?.dob);
    const doiParts = CaseDataTransformer.extractDateParts(patient?.doi || patient?.injuryDate);
    const sysDateParts = CaseDataTransformer.extractDateParts(now);
    const offWorkUntilParts = CaseDataTransformer.extractDateParts(ws.offWorkUntil);
    const offWorkFromParts = CaseDataTransformer.extractDateParts(ws.offWorkFrom);
    const returnToFullDutyOnParts = CaseDataTransformer.extractDateParts(ws.returnToFullDutyOn);
    const returnToModifiedWorkOnParts = CaseDataTransformer.extractDateParts(ws.returnToModifiedWorkOn);

    // Build the Canonical Semantic camelCase Aliases Object
    const canonicalAliases = {
      // Organization Details
      orgName,
      orgAddress,
      orgCity,
      orgState,
      orgZip,
      orgCityStZip,
      orgPhone,
      orgFax,
      orgFullAddress,

      // Patient Demographics
      patientName: patientFullName,
      patientNameFirstMiddleLast: patientFullName,
      patientNameLastFirst,
      patientNameLastFirstMi: patientNameLastFirst,
      employeeName: patientFullName,
      employeeNameLastFirst: patientNameLastFirst,
      patientFirstName,
      patientLastName,
      patientMiddleInitial,
      dob: CaseDataTransformer.formatDate(patient?.dob),
      dobMonth: dobParts.month,
      dobDay: dobParts.day,
      dobYear: dobParts.year,
      age,
      sex,
      patientSex: sex,
      ssn: ssnFormatted,
      l4ssn,
      driversLicense: patient?.driversLicense || patient?.dlNumber || '',
      patientAddress,
      patientCity,
      patientState,
      patientZip,
      patientAddressCityStZip,
      cellPhone,
      otherPhone,
      patientEmail,
      maritalStatus: patient?.maritalStatus || '',
      interpreter,
      interpreterRequired,
      interpreterLanguage,

      // Employer
      employerName,
      employerAddress,
      employerCity,
      employerState,
      employerZip,
      employerAddressCityStZip,
      employerPhone,
      patientOccupation,
      employmentStatus,

      // Workers' Comp Insurance
      wcInsuranceName,
      wcInsuranceAddress,
      wcInsuranceCity,
      wcInsuranceState,
      wcInsuranceZip,
      insuranceNameAddress,
      claimNumber,
      policyNumber,
      claimsExaminer,
      claimsExaminerAddress: wcInsuranceAddress,
      claimsExaminerCity: wcInsuranceCity,
      claimsExaminerState: wcInsuranceState,
      claimsExaminerZip: wcInsuranceZip,
      claimsExaminerPhone,
      claimsExaminerFax,
      claimsExaminerEmail,
      qmePanelNumber,
      panelNumber,
      dwcClaimNumber,
      ddAssignmentNumber,
      ddAssignmentNo: ddAssignmentNumber,
      ddAssignment: ddAssignmentNumber,
      dwcClaimNo: dwcClaimNumber || panelNumber || qmePanelNumber || patient?.dwcClaimNo || patient?.claimNo || '',

      // Case & Injury
      caseType,
      caseCategory,
      doi,
      doiMonth: doiParts.month,
      doiDay: doiParts.day,
      doiYear: doiParts.year,
      doa,
      ctInjuryCheckBox,
      doiEnd,
      bodyParts,
      injuryDescription,

      // Doctor
      treatingDoctor,
      doctorName,
      qmeName: doctorName,
      doctorFirstName,
      doctorLastName,
      doctorSpecialty,
      qmeSpecialty: doctorSpecialty,
      doctorLicense,
      qmeSln,
      doctorNpi,
      doctorPhone,
      qmePhone: doctorPhone,
      doctorFax,
      qmeFax: doctorFax,
      treatingDoctorPhone: doctorPhone,
      treatingDoctorFax: doctorFax,
      treatingDoctorSln: doctorLicense,
      treatingDoctorSignatureDate: dateSigned,
      docDate,
      dateSigned,
      signatureOnFile,
      userSignatureBase64: userSigBase64,
      doctorSignatureBase64: doctorSigBase64,

      // Appointment & Facility
      appointmentDate,
      appointmentDateMonth: apptDateParts.month,
      appointmentDateDay: apptDateParts.day,
      appointmentDateYear: apptDateParts.year,
      appointmentTime,
      appointmentDateTime,
      dischargeTime: appointmentTime,
      facilityName,
      facilityAddress,
      facilityCity,
      facilityState,
      facilityZip,
      facilityFullAddress,
      facilityPhone,
      facilityFax,

      // Attorneys
      attorneyName,
      attorneyFirmName,
      attorneyAddress,
      attorneyCity,
      attorneyState,
      attorneyZip,
      attorneyPhone,
      attorneyFax,
      attorneyEmail,
      servedAttorneyName: attorneyName,
      servedAttorneyAddress,
      servedAttorneyCity: attorneyCity,
      servedAttorneyState: attorneyState,
      servedAttorneyZip: attorneyZip,

      defenseAttorneyName,
      defenseAttorneyFirmName,
      defenseAttorneyAddress,
      defenseAttorneyCity,
      defenseAttorneyState,
      defenseAttorneyZip,
      defenseAttorneyPhone,
      defenseAttorneyFax,
      defenseAttorneyEmail,
      servedDefenseAttorneyName: defenseAttorneyName,
      servedDefenseAttorneyAddress,
      servedDefenseAttorneyCity: defenseAttorneyCity,
      servedDefenseAttorneyState: defenseAttorneyState,
      servedDefenseAttorneyZip: defenseAttorneyZip,

      servedPatientName: patientFullName,
      servedPatientAddress: patientAddress,
      servedPatientCity: patientCity,
      servedPatientState: patientState,
      servedPatientZip: patientZip,

      servedWcInsuranceName: wcInsuranceName,
      servedWcInsuranceAddress: wcInsuranceAddress,
      servedWcInsuranceCity: wcInsuranceCity,
      servedWcInsuranceState: wcInsuranceState,
      servedWcInsuranceZip: wcInsuranceZip,

      // Clinical
      subjective,
      objective,
      assessment,
      primaryDiagnosis,
      primaryIcd10,
      secondaryDiagnosis,
      secondaryIcd10,
      treatmentPlan,
      plan: treatmentPlan,

      // Work Status
      remainOffWorkCheckBox,
      offWorkUntil,
      offWorkUntilMonth: offWorkUntilParts.month,
      offWorkUntilDay: offWorkUntilParts.day,
      offWorkUntilYear: offWorkUntilParts.year,
      offWorkFromMonth: offWorkFromParts.month,
      offWorkFromDay: offWorkFromParts.day,
      offWorkFromYear: offWorkFromParts.year,
      disabilityPeriodStartDate: ws.offWorkFrom ? CaseDataTransformer.formatDate(ws.offWorkFrom) : '',
      disabilityPeriodEndDate: offWorkUntil,
      workRestrictionsUntilMonth: offWorkUntilParts.month,
      workRestrictionsUntilDay: offWorkUntilParts.day,
      workRestrictionsUntilYear: offWorkUntilParts.year,
      returnToModifiedWorkCheckBox,
      returnToModifiedWorkOn,
      returnToModifiedWorkOnMonth: returnToModifiedWorkOnParts.month,
      returnToModifiedWorkOnDay: returnToModifiedWorkOnParts.day,
      returnToModifiedWorkOnYear: returnToModifiedWorkOnParts.year,
      returnToFullDutyCheckBox,
      returnToFullDutyOn,
      returnToFullDutyOnMonth: returnToFullDutyOnParts.month,
      returnToFullDutyOnDay: returnToFullDutyOnParts.day,
      returnToFullDutyOnYear: returnToFullDutyOnParts.year,
      workRestrictions,
      maxLiftingWeight,
      noProlongedSittingCheckBox,
      noProlongedStandingCheckBox,
      noProlongedWalkingCheckBox,
      noTwistingCheckBox,
      noLiftingCheckBox,
      standing0HoursCheckBox: noProlongedStandingCheckBox,
      sitting0HoursCheckBox: noProlongedSittingCheckBox,
      twisting0HoursCheckBox: noTwistingCheckBox,
      walking0HoursCheckBox: noProlongedWalkingCheckBox,

      // System & Admin
      sysDate,
      sysDateMonth: sysDateParts.month,
      sysDateDay: sysDateParts.day,
      sysDateYear: sysDateParts.year,
      sysDate30,
      sysDate45,
      sysDate60,
      sysDate90,
      sysDateDisplay,
      sysDateDisplay30,
      sysDateDisplay45,
      sysDateDisplay60,
      sysDateDisplay90,
      author: userName,
      userName,
      userEmail,
    };

    // 13. Build composite tree for FlattUtil.is() (Underscore Flattened Dictionary)
    const compositeTree: any = {
      ...patient,
      org: orgData,
      provider: doc
        ? {
            ...doc,
            name: NameUtil.getFormattedDoctorName(doc.name || doc.displayName || doctorName),
          }
        : null,
      doctorSign: doctorSignature,
      userSign: userSignature,
      selectedAppointment: selectedAppointment
        ? {
            ...selectedAppointment,
            provider: doc
              ? {
                  ...doc,
                  name: NameUtil.getFormattedDoctorName(doc.name || doc.displayName || doctorName),
                }
              : null,
            facility: fac
              ? {
                  ...fac,
                  zip: facilityZip || fac.zip,
                }
              : null,
          }
        : null,
      lastEncounter: latestEncounter,
      workStatus: ws,
      forms: historyForms,
      author: userName,
      age,
      sysDate: sysDateDisplay,
      sysDate30: sysDateDisplay30,
      sysDate45: sysDateDisplay45,
      sysDate60: sysDateDisplay60,
      sysDate90: sysDateDisplay90,
      treatingDoctor,
    };

    // Clean unwanted nodes from composite tree before flattening
    CaseDataTransformer.UNWANTED_NODES.forEach((key) => {
      delete compositeTree[key];
    });
    const cleanedTree = CaseDataTransformer.removeNullValues(compositeTree);

    // Generate flattened underscore dictionary for HTML TinyMCE templates
    const flattenedData = FlattUtil.is(cleanedTree);

    // Ensure phone/fax fields in flattened dictionary are formatted (XXX) XXX-XXXX
    Object.keys(flattenedData).forEach((key) => {
      const lower = key.toLowerCase();
      if (lower.includes('phone') || lower.includes('fax')) {
        flattenedData[key] = CaseDataTransformer.formatPhone(flattenedData[key]);
      }
    });

    // 14. Return the Unified ComposedCaseData object
    return {
      // Flattened underscore dictionary
      ...flattenedData,
      // Canonical semantic camelCase aliases
      ...canonicalAliases,
      // Raw structured trees
      patient,
      org: orgData,
      provider: doc,
      appointment: selectedAppointment,
      facility: fac,
      lastEncounter: latestEncounter,
      workStatus: ws,
      historyForms,
      doctorSignature,
      userSignature,
    } as ComposedCaseData;
  }

  public static formatDate(val: any, targetFormat: string = 'MM/dd/yyyy'): string {
    if (!val) return '';
    try {
      const d = new Date(val);
      if (isNaN(d.getTime())) return '';
      return format(d, targetFormat);
    } catch {
      return '';
    }
  }

  public static extractDateParts(val: any): { month: string; day: string; year: string } {
    if (!val) return { month: '', day: '', year: '' };
    try {
      const d = new Date(val);
      if (isNaN(d.getTime())) return { month: '', day: '', year: '' };
      return {
        month: format(d, 'MM'),
        day: format(d, 'dd'),
        year: format(d, 'yyyy'),
      };
    } catch {
      return { month: '', day: '', year: '' };
    }
  }

  public static formatPatientNames(patient: any): {
    firstName: string;
    lastName: string;
    middleInitial: string;
    firstMiddleLast: string;
    lastFirstMi: string;
  } {
    let first = (patient?.firstName || '').trim();
    let last = (patient?.lastName || '').trim();
    let mi = (patient?.middleInitial || patient?.middleName || '').replace(/\./g, '').trim();
    if (mi) {
      mi = mi.charAt(0).toUpperCase();
    }

    if (!first && !last && patient?.name) {
      const raw = patient.name.trim();
      if (raw.includes(',')) {
        const parts = raw.split(',').map((p: string) => p.trim()).filter(Boolean);
        last = parts[0] || '';
        if (parts.length >= 3) {
          first = parts[1] || '';
          mi = parts[2] ? parts[2].charAt(0).toUpperCase() : '';
        } else if (parts.length === 2) {
          const firstParts = parts[1].split(/\s+/);
          first = firstParts[0] || '';
          if (firstParts.length > 1 && !mi) {
            mi = firstParts[1].charAt(0).toUpperCase();
          }
        }
      } else {
        const parts = raw.split(/\s+/).filter(Boolean);
        if (parts.length === 1) {
          first = parts[0];
        } else if (parts.length === 2) {
          first = parts[0];
          last = parts[1];
        } else if (parts.length >= 3) {
          first = parts[0];
          mi = parts[1].charAt(0).toUpperCase();
          last = parts.slice(2).join(' ');
        }
      }
    }

    let firstMiddleLast = '';
    if (first && last) {
      firstMiddleLast = mi ? `${first} ${mi} ${last}` : `${first} ${last}`;
    } else if (first || last) {
      firstMiddleLast = first || last;
    } else {
      firstMiddleLast = patient?.name || '';
    }

    let lastFirstMi = '';
    if (last && first) {
      lastFirstMi = mi ? `${last}, ${first}, ${mi}` : `${last}, ${first}`;
    } else if (last || first) {
      lastFirstMi = last || first;
    } else {
      lastFirstMi = patient?.name || '';
    }

    return {
      firstName: first,
      lastName: last,
      middleInitial: mi,
      firstMiddleLast,
      lastFirstMi,
    };
  }

  public static formatTime(val: any): string {
    if (!val) return '';
    try {
      const d = new Date(val);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return '';
    }
  }

  public static formatPhone(input: string | number): string {
    if (!input) return '';
    const str = typeof input !== 'string' ? input.toString() : input;
    const clean = str.replace(/\D/g, '');
    if (clean.length === 10) {
      return clean.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
    }
    return str.trim();
  }

  public static formatSSN(input: string | number): string {
    if (!input) return '';
    const clean = (typeof input !== 'string' ? input.toString() : input).replace(/\D/g, '');
    if (clean.length === 9) {
      return clean.replace(/(\d{3})(\d{2})(\d{4})/, '$1-$2-$3');
    }
    return clean;
  }

  private static removeNullValues(data: any): any {
    if (!data || typeof data !== 'object') return data;
    const clean: any = Array.isArray(data) ? [] : {};
    Object.keys(data).forEach((key) => {
      const val = data[key];
      if (val !== null && val !== undefined) {
        clean[key] = typeof val === 'object' && !(val instanceof Date) ? CaseDataTransformer.removeNullValues(val) : val;
      }
    });
    return clean;
  }
}
